/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useImperativeHandle
} from 'react';
import FilterChecklist, {
  FilterChecklistValue
} from '@ion/dls/src/filters/FilterChecklist';
import {
  TrackingDrawerFilterHandleBase,
  TrackingDrawerFilterOptionBase,
  TrackingDrawerFilterPropsBase
} from './types';
import useTrackingDrawerFilter from './useTrackingDrawerFilter';

export interface TrackingDrawerFilterHandle extends TrackingDrawerFilterHandleBase {}

export interface TrackingDrawerFilterOption extends TrackingDrawerFilterOptionBase {}

export interface TrackingDrawerFilterProps extends TrackingDrawerFilterPropsBase {}

/**
 * Displays one checklist-backed filter section for the tracking filter drawer.
 *
 * ```typescript
 * import TrackingDrawerFilter from './TrackingDrawerFilter';
 *
 * ...
 *
 * <TrackingDrawerFilter ref={filterRef}
 *   applyFilter={setAssetTypeFilters}
 *   loadOptions={loadAssetTypes}
 *   selectedItems={selectedAssetTypes}
 *   searchPlaceholder='Search asset type...' />
 * ```
 */
export const TrackingDrawerFilter = forwardRef<TrackingDrawerFilterHandle, TrackingDrawerFilterProps>(({
  applyFilter,
  emptyState,
  filterStateKey,
  loadOptions,
  onSelectedCountChange,
  onSelectionChange,
  searchPlaceholder,
  selectedItems,
  toSelectedValue
}, ref) => {
  const {
    applySelectedValues,
    clear,
    discard,
    error,
    getFavoriteState,
    handleChange,
    loadFavoriteState,
    loading,
    options,
    hasChanges,
    selectedValues
  } = useTrackingDrawerFilter({
    applyFilter,
    filterStateKey,
    loadOptions,
    onSelectedCountChange,
    onSelectionChange,
    selectedItems,
    toSelectedValue
  });

  useImperativeHandle(ref, () => ({
    apply : () => applySelectedValues(),
    clear,
    discard,
    getFavoriteState,
    loadFavoriteState,
    hasChanges
  }), [
    applySelectedValues,
    clear,
    discard,
    getFavoriteState,
    hasChanges,
    loadFavoriteState
  ]);

  return (
    <FilterChecklist searchable searchWhenOverflowing
      options={options}
      selectedValues={selectedValues as FilterChecklistValue[]}
      searchPlaceholder={searchPlaceholder}
      emptyState={emptyState}
      loading={loading}
      error={error}
      onChange={handleChange} />
  );
});

export default memo(TrackingDrawerFilter);
